/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from 'src/utils/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from 'src/utils/logicTypes';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IIndividualTermsState {
    version: Dtos.Version | null;
    category: Dtos.Category | null;
    term: Dtos.Term | null;
    terms: Record<number, Dtos.Term>;
    view: number[];
    loadState: IRequestState;
}

interface IInitialTermsState {
    byCategory: Record<number, IIndividualTermsState>;
    byTerm: Record<number, IIndividualTermsState>;
}

export interface ITermsState {
   terms: IInitialTermsState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const TermsInitialIndividualState: IIndividualTermsState = {
    version: null,
    category: null,
    term: null,
    terms: {},
    view: [],
    loadState: {
        state: RequestState.None
    }
};

const TermsInitialState: IInitialTermsState = {
    byCategory: {},
    byTerm: {}
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useTermsSelector: TypedUseSelectorHook<ITermsState> = useSelector;

export const TermsSelectors = {
    byCategory: {
        selectTermsVersion: (state: ITermsState, categoryId: number) => state.terms.byCategory[categoryId] ? state.terms.byCategory[categoryId].version : TermsInitialIndividualState.version,
        selectTermsCategory: (state: ITermsState, categoryId: number) => state.terms.byCategory[categoryId] ? state.terms.byCategory[categoryId].category : TermsInitialIndividualState.category,
        selectTermsTerm: (state: ITermsState, categoryId: number) => state.terms.byCategory[categoryId] ? state.terms.byCategory[categoryId].term : TermsInitialIndividualState.term,
        selectTermsView: (state: ITermsState, categoryId: number) => state.terms.byCategory[categoryId] ? state.terms.byCategory[categoryId].view : TermsInitialIndividualState.view,
        selectTerm: (state: ITermsState, categoryId: number, termId: number) => state.terms.byCategory[categoryId]  && state.terms.byCategory[categoryId].terms[termId] ? state.terms.byCategory[categoryId].terms[termId] : null,
        selectTermsLoadState: (state: ITermsState, categoryId: number) => state.terms.byCategory[categoryId] ? state.terms.byCategory[categoryId].loadState : TermsInitialIndividualState.loadState
    },
    byTerm: {
        selectTermsVersion: (state: ITermsState, termId: number) => state.terms.byTerm[termId] ? state.terms.byTerm[termId].version : TermsInitialIndividualState.version,
        selectTermsCategory: (state: ITermsState, termId: number) => state.terms.byTerm[termId] ? state.terms.byTerm[termId].category : TermsInitialIndividualState.category,
        selectTermsTerm: (state: ITermsState, termId: number) => state.terms.byTerm[termId] ? state.terms.byTerm[termId].term : TermsInitialIndividualState.term,
        selectTermsView: (state: ITermsState, termId: number) => state.terms.byTerm[termId] ? state.terms.byTerm[termId].view : TermsInitialIndividualState.view,
        selectTerm: (state: ITermsState, termId: number, subtermId: number) => state.terms.byCategory[termId]  && state.terms.byCategory[termId].terms[subtermId] ? state.terms.byCategory[termId].terms[subtermId] : null,
        selectTermsLoadState: (state: ITermsState, termId: number) => state.terms.byTerm[termId] ? state.terms.byTerm[termId].loadState : TermsInitialIndividualState.loadState
    }
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class TermReducer extends ImmerReducer<IInitialTermsState> {
    public loadByCategoryId(categoryId: number) {
        if (!this.draftState.byCategory[categoryId]) {
            this.draftState.byCategory[categoryId] = {...TermsInitialIndividualState};
        }

        this.draftState.byCategory[categoryId].loadState = {
            state: RequestState.Pending
        };
    }

    public loadByTermId(termId: number) {
        if (!this.draftState.byTerm[termId]) {
            this.draftState.byTerm[termId] = {...TermsInitialIndividualState};
        }

        this.draftState.byTerm[termId].loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(version: Dtos.Version, category: Dtos.Category, term: Dtos.Term | null, terms: Dtos.Term[]) {
        const type = term ? 'byTerm' : 'byCategory';
        const id = term ? term.id : category.id;
        if (!this.draftState[type][id]) {
            this.draftState[type][id] = {...TermsInitialIndividualState};
        }

        this.draftState[type][id].version = version;
        this.draftState[type][id].category = category;
        this.draftState[type][id].term = term;

        this.draftState[type][id].terms = {};

        this.draftState[type][id].view = [];

        for (const t of terms) {
            this.draftState[type][id].terms[t.id] = t;
            this.draftState[type][id].view.push(t.id);
        }

        this.draftState[type][id].loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(type: 'byCategory' | 'byTerm', id: number, responseStatus: Dtos.ResponseStatus) {
        if (!this.draftState[id]) {
            this.draftState[id] = {...TermsInitialIndividualState};
        }

        this.draftState[type][id].version = TermsInitialIndividualState.version;
        this.draftState[type][id].category = TermsInitialIndividualState.category;
        this.draftState[type][id].term = TermsInitialIndividualState.term;
        this.draftState[type][id].terms = TermsInitialIndividualState.terms;
        this.draftState[type][id].view = TermsInitialIndividualState.view;
        this.draftState[type][id].loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear(type: 'byCategory' | 'byTerm', id: number) {
        delete this.draftState[type][id];
    }

    public clearAllByType(type: 'byCategory' | 'byTerm') {
        this.draftState[type] = TermsInitialState[type];
    }

    public clearAll() {
        return {...TermsInitialState};
    }
}

export const TermsActions = createActionCreators(TermReducer);
const reducer = createReducerFunction(TermReducer, TermsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const TermsApi = {
    loadByCategoryId: createGetRequest(
        Dtos.GetTermsByCategoryId,
        (categoryId: number) => ({
            categoryId
        })
    ),
    loadByTermId: createGetRequest(
        Dtos.GetTermsByTermId,
        (termId: number) => ({
            termId
        })
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const TermsLogic = {
    loadByCategoryId: createLogic({
        type: TermsActions.loadByCategoryId.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<ITermsState, typeof TermsActions.loadByCategoryId>, dispatch, done) => {
            try {
                const response = await TermsApi.loadByCategoryId(action.payload);

                dispatch(TermsActions.loadSuccess(
                    response.version,
                    response.category,
                    response.parentTerm ? response.parentTerm : null,
                    response.terms
                ));
            }
            catch (error) {
                dispatch(TermsActions.loadFailure('byTerm', action.payload, error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByTermId: createLogic({
        type: TermsActions.loadByTermId.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<ITermsState, typeof TermsActions.loadByTermId>, dispatch, done) => {
            try {
                const response = await TermsApi.loadByTermId(action.payload);

                dispatch(TermsActions.loadSuccess(
                    response.version,
                    response.category,
                    response.parentTerm ? response.parentTerm : null,
                    response.terms
                ));
            }
            catch (error) {
                dispatch(TermsActions.loadFailure('byTerm', action.payload, error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    TermsLogic.loadByCategoryId,
    TermsLogic.loadByTermId
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('terms', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/reducers/terms.ts